import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../common/AuthContext';
import { useTimer } from './TimerContext';
import { authFetch } from '../common/authFetch';
import { getSubjectIcon } from '../common/subjectIconMap';
import { appendFrame } from '../utils/timelapseDB';
import PeerHost from '../components/PeerHost';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIMELAPSE_CAPTURE_WIDTH = 640;
const TIMELAPSE_CAPTURE_HEIGHT = 480;
const TIMELAPSE_CAPTURE_INTERVAL = 30000; // 30초
const TIMELAPSE_MAX_FRAMES = 720;

const StudyRoomContext = createContext();

export const useStudyRoom = () => useContext(StudyRoomContext);

// 타이머 정보를 Context 내부 ref에 동기화하는 컴포넌트
// Provider가 useTimer()를 직접 호출하면 displaySeconds 변경 시 매초 re-render 발생
// 이 컴포넌트만 매초 re-render되고, Provider와 Consumer는 영향 없음
const TimerInfoSync = React.memo(({ timerInfoRef }) => {
    const { activeSession, displaySeconds } = useTimer();
    useEffect(() => {
        timerInfoRef.current = { session: activeSession, seconds: displaySeconds };
    }, [activeSession, displaySeconds, timerInfoRef]);
    return null;
});

export const StudyRoomProvider = ({ children }) => {
    const { user } = useAuth();
    const [myRoomOpen, setMyRoomOpen] = useState(false);
    const myRoomOpenRef = useRef(false);
    const snapshotBlobRef = useRef(null);
    const [studyStatus, setStudyStatus] = useState({});

    // 카메라 상태
    const [cameraOn, setCameraOn] = useState(false);
    const [liveStream, setLiveStream] = useState(null);
    const [facingMode, setFacingMode] = useState('user');
    const [roomOpenTime, setRoomOpenTime] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const intervalRef = useRef(null);

    // ─── 타임랩스 녹화 ───
    const tlActiveRef = useRef(false);
    const tlClosingRef = useRef(false);
    const [tlIsRecording, setTlIsRecording] = useState(false);
    const [tlFrameCount, setTlFrameCount] = useState(0);
    const tlWorkerRef = useRef(null);
    const tlVisibilityHandlerRef = useRef(null);
    const tlCaptureRef = useRef(null); // captureFrame 클로저 보관

    // 타이머 정보 ref (TimerInfoSync가 업데이트)
    const timerInfoRef = useRef({ session: null, seconds: 0 });

    const startTimelapseRecording = useCallback(() => {
        if (tlActiveRef.current) return;

        tlActiveRef.current = true;
        setTlFrameCount(0);
        setTlIsRecording(true);

        const captureCanvas = document.createElement('canvas');
        captureCanvas.width = TIMELAPSE_CAPTURE_WIDTH;
        captureCanvas.height = TIMELAPSE_CAPTURE_HEIGHT;
        const ctx = captureCanvas.getContext('2d');
        let framesCaptured = 0;
        let lastCaptureTime = 0;

        const captureFrame = () => {
            if (!tlActiveRef.current) return;
            const video = videoRef.current;
            if (!video || video.readyState < video.HAVE_CURRENT_DATA) {
                console.warn('[TL] captureFrame skipped: video not ready', video?.readyState);
                return;
            }
            if (framesCaptured >= TIMELAPSE_MAX_FRAMES) return;

            // 최소 간격 보장 (Worker + visibilitychange 중복 방지)
            const now = Date.now();
            if (now - lastCaptureTime < TIMELAPSE_CAPTURE_INTERVAL * 0.8 && lastCaptureTime > 0) return;
            lastCaptureTime = now;

            ctx.drawImage(video, 0, 0, TIMELAPSE_CAPTURE_WIDTH, TIMELAPSE_CAPTURE_HEIGHT);

            captureCanvas.toBlob((blob) => {
                if (!blob || !tlActiveRef.current) return;

                const { session, seconds } = timerInfoRef.current;
                const timerInfo = session ? {
                    displaySeconds: seconds,
                    subjectName: session.subject_name || '',
                    subjectColor: session.subject_color || '#ccc',
                    subjectIcon: getSubjectIcon(session.subject_name),
                    status: session.status || 'active'
                } : null;

                appendFrame(blob, timerInfo).catch((err) => {
                    console.warn('[TL] appendFrame failed:', err);
                });

                framesCaptured++;
                setTlFrameCount(framesCaptured);
                console.log(`[TL] frame captured: #${framesCaptured}`);
            }, 'image/jpeg', 0.7);
        };

        tlCaptureRef.current = captureFrame;

        // Web Worker로 타이머 실행 (백그라운드 탭에서도 스로틀링 안됨)
        try {
            const worker = new Worker('/timelapse-worker.js');
            worker.onmessage = (e) => {
                if (e.data.type === 'capture' && tlActiveRef.current) {
                    captureFrame();
                }
            };
            worker.onerror = (err) => {
                console.warn('[TL] Worker error, falling back to setInterval:', err);
                // Worker 실패 시 setInterval 폴백
                worker.terminate();
                tlWorkerRef.current = null;
                const fallbackId = setInterval(captureFrame, TIMELAPSE_CAPTURE_INTERVAL);
                tlWorkerRef.current = { terminate: () => clearInterval(fallbackId) };
            };
            worker.postMessage({ type: 'start', interval: TIMELAPSE_CAPTURE_INTERVAL });
            tlWorkerRef.current = worker;
        } catch {
            // Worker 지원 안 되는 환경 — setInterval 폴백
            console.warn('[TL] Worker not supported, using setInterval');
            const fallbackId = setInterval(captureFrame, TIMELAPSE_CAPTURE_INTERVAL);
            tlWorkerRef.current = { terminate: () => clearInterval(fallbackId) };
        }

        // 백그라운드 탭 복귀 시 캡처 보상 (Worker + visibilitychange 이중 보장)
        const handleVisibility = () => {
            if (!document.hidden && tlActiveRef.current) {
                captureFrame();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        tlVisibilityHandlerRef.current = handleVisibility;
    }, []);

    const stopTimelapseRecording = useCallback(() => {
        tlActiveRef.current = false;
        if (tlWorkerRef.current) {
            tlWorkerRef.current.terminate();
            tlWorkerRef.current = null;
        }
        if (tlVisibilityHandlerRef.current) {
            document.removeEventListener('visibilitychange', tlVisibilityHandlerRef.current);
            tlVisibilityHandlerRef.current = null;
        }
        tlCaptureRef.current = null;
        setTlIsRecording(false);
    }, []);

    const setTimelapseClosing = useCallback((closing) => {
        tlClosingRef.current = closing;
    }, []);

    // 카메라 켜질 때 타임랩스 녹화 자동 시작/중지
    useEffect(() => {
        if (cameraOn && myRoomOpen && !tlActiveRef.current && !tlClosingRef.current) {
            startTimelapseRecording();
        }
        if (!cameraOn && tlActiveRef.current) {
            stopTimelapseRecording();
        }
    }, [cameraOn, myRoomOpen, startTimelapseRecording, stopTimelapseRecording]);

    // myRoomOpenRef 동기화
    useEffect(() => {
        myRoomOpenRef.current = myRoomOpen;
    }, [myRoomOpen]);

    // 마운트 시 서버에서 내 방 상태 확인
    useEffect(() => {
        if (!user) return;
        const checkStatus = async () => {
            try {
                const response = await authFetch(`${API_URL}/api/v1/studyrooms/my-status`);
                const data = await response.json();
                if (data.success && data.isOpen) {
                    setMyRoomOpen(true);
                }
            } catch {
                // silent
            }
        };
        checkStatus();
    }, [user]);

    // 카메라 시작
    const startCamera = useCallback(async (facing) => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        const mode = facing || facingMode;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480, facingMode: mode }
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            setCameraOn(true);
            setLiveStream(stream);
            setFacingMode(mode);
            sessionStorage.setItem('studyroom_camera', 'on');
            sessionStorage.setItem('studyroom_facing', mode);
        } catch (error) {
            console.error('Camera access denied:', error);
            setCameraOn(false);
            sessionStorage.setItem('studyroom_camera', 'off');
        }
    }, [facingMode]);

    // 카메라 정지
    const stopCamera = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setCameraOn(false);
        setLiveStream(null);
        snapshotBlobRef.current = null;
        sessionStorage.setItem('studyroom_camera', 'off');
    }, []);

    const toggleCamera = useCallback(() => {
        if (cameraOn) stopCamera();
        else startCamera();
    }, [cameraOn, startCamera, stopCamera]);

    const switchCamera = useCallback(() => {
        const newMode = facingMode === 'user' ? 'environment' : 'user';
        startCamera(newMode);
    }, [facingMode, startCamera]);

    // cameraOn + video 엘리먼트 준비 → stream 연결 + 스냅샷 interval 시작
    useEffect(() => {
        if (!cameraOn || !streamRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        video.srcObject = streamRef.current;

        const startSnapshotInterval = () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                if (!canvasRef.current || !videoRef.current) return;
                const v = videoRef.current;
                if (v.readyState < v.HAVE_CURRENT_DATA) return;
                canvasRef.current.width = 320;
                canvasRef.current.height = 240;
                const snapshotCtx = canvasRef.current.getContext('2d');
                snapshotCtx.drawImage(v, 0, 0, 320, 240);
                canvasRef.current.toBlob((blob) => {
                    if (blob) snapshotBlobRef.current = blob;
                }, 'image/jpeg', 0.5);
            }, 1000);
        };

        if (video.readyState >= video.HAVE_CURRENT_DATA) {
            startSnapshotInterval();
        } else {
            video.addEventListener('loadeddata', startSnapshotInterval, { once: true });
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [cameraOn, myRoomOpen, liveStream]);

    // 방 열릴 때 카메라 자동 시작
    useEffect(() => {
        if (myRoomOpen && sessionStorage.getItem('studyroom_camera') === 'on') {
            const savedFacing = sessionStorage.getItem('studyroom_facing') || 'user';
            startCamera(savedFacing);
        }
    }, [myRoomOpen, startCamera]);

    // 방 닫힐 때 카메라 정리
    useEffect(() => {
        if (!myRoomOpen && streamRef.current) {
            stopCamera();
        }
    }, [myRoomOpen, stopCamera]);

    // 브라우저 닫기/새로고침 시 방 닫기
    useEffect(() => {
        const closeRoomOnExit = () => {
            if (!myRoomOpenRef.current) return;
            tlActiveRef.current = false;
            if (tlWorkerRef.current) {
                tlWorkerRef.current.terminate();
                tlWorkerRef.current = null;
            }
            if (tlVisibilityHandlerRef.current) {
                document.removeEventListener('visibilitychange', tlVisibilityHandlerRef.current);
                tlVisibilityHandlerRef.current = null;
            }
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
            sessionStorage.removeItem('studyroom_camera');
            const token = localStorage.getItem('accessToken');
            if (!token) return;
            fetch(`${API_URL}/api/v1/studyrooms/close`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                keepalive: true
            }).catch(() => {});
        };

        window.addEventListener('beforeunload', closeRoomOnExit);
        window.addEventListener('pagehide', closeRoomOnExit);
        return () => {
            window.removeEventListener('beforeunload', closeRoomOnExit);
            window.removeEventListener('pagehide', closeRoomOnExit);
        };
    }, []);

    const openRoom = useCallback(async () => {
        try {
            const response = await authFetch(`${API_URL}/api/v1/studyrooms/open`, { method: 'POST' });
            const data = await response.json();
            if (data.success) {
                setMyRoomOpen(true);
                setRoomOpenTime(Date.now());
                sessionStorage.removeItem('studyroom_camera');
                snapshotBlobRef.current = null;
                return true;
            }
        } catch (error) {
            console.error('Failed to open room:', error);
        }
        return false;
    }, []);

    const closeRoom = useCallback(async () => {
        try {
            const response = await authFetch(`${API_URL}/api/v1/studyrooms/close`, { method: 'POST' });
            const data = await response.json();
            if (data.success) {
                stopCamera();
                setMyRoomOpen(false);
                setRoomOpenTime(null);
                sessionStorage.removeItem('studyroom_camera');
                snapshotBlobRef.current = null;
                return data.sessionId || null;
            }
        } catch (error) {
            console.error('Failed to close room:', error);
        }
        return null;
    }, [stopCamera]);

    // useMemo로 context value 안정화 — displaySeconds 변경 시 re-render 방지
    const contextValue = useMemo(() => ({
        myRoomOpen, openRoom, closeRoom,
        snapshotBlobRef, studyStatus, setStudyStatus,
        cameraOn, liveStream, toggleCamera, switchCamera, facingMode,
        videoRef, roomOpenTime,
        // 타임랩스
        tlIsRecording, tlFrameCount,
        startTimelapseRecording, stopTimelapseRecording, setTimelapseClosing
    }), [
        myRoomOpen, openRoom, closeRoom,
        studyStatus, cameraOn, liveStream, toggleCamera, switchCamera, facingMode,
        roomOpenTime,
        tlIsRecording, tlFrameCount,
        startTimelapseRecording, stopTimelapseRecording, setTimelapseClosing
    ]);

    return (
        <StudyRoomContext.Provider value={contextValue}>
            <TimerInfoSync timerInfoRef={timerInfoRef} />
            {children}
            {myRoomOpen && (
                <>
                    <PeerHost
                        snapshotBlobRef={snapshotBlobRef}
                        studyStatus={studyStatus}
                    />
                    <video ref={videoRef} autoPlay playsInline muted
                        style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1, opacity: 0.01, pointerEvents: 'none', zIndex: -1 }} />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </>
            )}
        </StudyRoomContext.Provider>
    );
};
